import {match, Pattern} from 'ts-pattern';

// eslint-disable-next-line no-restricted-imports -- Intended
import {always, isNil, pathOr} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {FILTER_VALUE_KEY} from '../constants/filterValueKey';
import type {EagFilterProps} from '../types/EagFilterProps';
import {makeOnChipsChange} from './makeOnChipsChange';
import {getQuickFilterFromIntersection} from './quickFilterValue';

type DateRangeObject = {from: string | null; to: string | null};
type RelativeInputModel = {relativeInputValue: number; relativeInputKey: string};
type DateRangeModel = DateRangeObject | RelativeInputModel | string | null;

type DateRangeFilterConfig = {
  value: EagFilterProps['value'];
  onChange: EagFilterProps['onChange'];
  dateFormatter: (date: Date) => string;
  hasRelativeInputs: boolean;
};

const isRelativeValue = (model: unknown): model is RelativeInputModel =>
  isNotNilOrEmpty((model as RelativeInputModel)?.relativeInputKey);

export const useDateRangeFilter = (config: DateRangeFilterConfig) => {
  const model = config.value[FILTER_VALUE_KEY] as DateRangeModel;

  const setFilterValue = (value: DateRangeModel) => {
    const newModel = match(value)
      .with(Pattern.nullish, always(null))
      .with(Pattern.string, (stringValue) => stringValue)
      .with({from: Pattern.any, to: Pattern.any}, (objectValue) => {
        if (isNil(objectValue.from) && isNil(objectValue.to)) {
          return null;
        }
        return objectValue;
      })
      .otherwise(always(value));

    config.onChange({[FILTER_VALUE_KEY]: newModel});
  };

  const onChipsChange = makeOnChipsChange<DateRangeModel>(setFilterValue, null);

  const onDateFromChange = (value: Date | null) => {
    setFilterValue({
      from: value ? config.dateFormatter(value) : null,
      to: pathOr(null, ['to'], model),
    });
  };

  const onDateToChange = (value: Date | null) => {
    setFilterValue({
      to: value ? config.dateFormatter(value) : null,
      from: pathOr(null, ['from'], model),
    });
  };

  const onInputChange = (relativeInputValue: number, relativeInputKey: string) => {
    const newModel =
      isNaN(relativeInputValue) || relativeInputValue === 0
        ? null
        : {relativeInputValue, relativeInputKey};

    setFilterValue(newModel);
  };

  const fromPickerValue = pathOr(null, ['from'], model);
  const toPickerValue = pathOr(null, ['to'], model);
  const relativeInput = isRelativeValue(model) ? model : null;
  const chipsValue = typeof model === 'string' ? [model] : [];
  const quickFilterValue = getQuickFilterFromIntersection(model);

  return {
    model,
    setFilterValue,
    onChipsChange,
    onDateFromChange,
    onDateToChange,
    onInputChange,
    fromPickerValue,
    toPickerValue,
    relativeInput,
    chipsValue,
    quickFilterValue,
  } as const;
};
